// Central place that decides the "status" (and therefore the color) of a
// run/comparison operation from the message codes it produced.
//
// Why this exists:
//  - An operation's result is a list of groups: [{ error_id, data: [...] }, ...]
//  - Historically any group with rows turned the operation RED. That is wrong
//    for operations whose result also carries purely informational groups
//    (e.g. C-OP1 / C-OP2: only their own code is an actual finding, the rest
//    is context/info).
//  - We will also need more states than ok/error soon (e.g. orange warnings),
//    so status is modeled as an ordered severity instead of a boolean.

export const SEVERITY = {
    OK: 'ok',          // completed, nothing to report
    INFO: 'info',      // completed, only informational messages (not a problem)
    WARNING: 'warning',// completed, needs attention (reserved for future use)
    ERROR: 'error',    // completed, a real finding
}

// Higher number = more severe. Used to aggregate a group header from its
// children (the header takes the worst severity among completed children).
export const SEVERITY_RANK = {
    [SEVERITY.OK]: 0,
    [SEVERITY.INFO]: 1,
    [SEVERITY.WARNING]: 2,
    [SEVERITY.ERROR]: 3,
}

// Single source of truth for status colors. Add/adjust here when new states
// are introduced; nothing else needs to change.
const COLORS = {
    [SEVERITY.OK]: '#27ae60',      // green
    [SEVERITY.INFO]: '#2e86de',    // blue – informational, not a problem
    [SEVERITY.WARNING]: '#f0932b', // orange – reserved for future rules
    [SEVERITY.ERROR]: '#ee5253',   // red
}

export const getSeverityColor = (severity) => COLORS[severity] ?? COLORS[SEVERITY.OK]

// Per-operation rules, keyed by the operation `key` (see Operations.js).
//
// When an operation has a rule, ONLY the listed message codes drive its
// severity; every other group it produces is treated as informational
// (stays green). Operations WITHOUT a rule keep the legacy behavior:
// any group with rows => ERROR.
//
// `error_id` here is the raw code present in result groups (the
// MessageCodes.*.error_id value), with the user-facing code in the comment.
const OPERATION_RULES = {
    // C-OP1 (100yr WSEL): only "C-OP 01" is a finding.
    'C11': {
        [SEVERITY.ERROR]: ['T-WSEL-01'], // C-OP 01
    },
    // C-OP2 (FW WSEL): only "C-OP 02" is a finding ("C-OP 03" is info).
    'C-OP2': {
        [SEVERITY.ERROR]: ['C-OP 02'], // C-OP 02
    },
}

const groupHasRows = (group) => Array.isArray(group?.data) && group.data.length > 0

// Severity is evaluated from worst to best so the first match wins.
const RULE_SEVERITY_ORDER = [SEVERITY.ERROR, SEVERITY.WARNING, SEVERITY.INFO]

// Compute the severity of an operation child from its result groups.
// Safe to call on freshly-run and history-loaded children alike, since it
// derives everything from `child.data.result` (the source of truth).
export const computeSeverity = (child) => {
    const result = child?.data?.result
    // Informational items (e.g. the Overview group) are never a "pass/fail"
    // check: with no finding they are INFO (blue) rather than OK (green).
    if (!Array.isArray(result) || result.length === 0)
        return child?.informational ? SEVERITY.INFO : SEVERITY.OK

    const rule = OPERATION_RULES[child?.key]

    // No rule: legacy behavior – any group with rows is an error.
    if (!rule)
        return result.some(groupHasRows) ? SEVERITY.ERROR : SEVERITY.OK

    // Rule-based: a configured code with rows sets that severity.
    for (const severity of RULE_SEVERITY_ORDER) {
        const codes = rule[severity]
        if (codes && result.some(g => codes.includes(g?.error_id) && groupHasRows(g)))
            return severity
    }

    // Has output but only non-configured (informational) groups.
    return result.some(groupHasRows) ? SEVERITY.INFO : SEVERITY.OK
}

export const isErrorSeverity = (severity) => severity === SEVERITY.ERROR

// Worst severity among the COMPLETED children – used for group headers.
export const aggregateSeverity = (children, isCompleted) => {
    let worst = SEVERITY.OK
    ;(children || []).forEach(child => {
        if (!isCompleted(child)) return
        const sev = computeSeverity(child)
        if (SEVERITY_RANK[sev] > SEVERITY_RANK[worst])
            worst = sev
    })
    return worst
}
